import { groupBy, keyBy, mapValues, noop, partition, set } from 'lodash/fp';
import { createContext, Dispatch, MutableRefObject, RefObject, SetStateAction } from 'react';

import type {
  PreviewPageMap,
  StepPreviewMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { WorkflowVersionMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  getBranchToken,
  getReorderedSteps,
  mapStepsPreviewAndToken,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import { SelectedStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/InquiryTemplateScreenEditor';
import {
  Column,
  CombinedVersionDiffStepForTree,
  SetDiff,
  ValueDiff,
  VersionDiffChangeType,
  VersionDiffCombinedStep,
  VersionDiffLinkedWorkflowStep,
  VersionDiffTreeNode,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import {
  firstInitialStep,
  uiStepAllComponentConfigs,
} from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import { isUpdateInquiryNextStepActionStep } from 'dashboard/apps/InquiryTemplateEditor/utils/workflow-steps';
import { InquiryTemplateVersionStep } from 'dashboard/models/inquiry-template-version';
import {
  WorkflowStep,
  WorkflowStepConditional,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { UiStepType } from 'lib/interfaces/StepsConfig';
import { isUiTransitionComponentConfig, UiComponentConfigType } from 'lib/interfaces/ui-components';
import { assertNever } from 'lib/utils/types';

const PLACEHOLDER_STEP_NAME = 'placeholder_step_name';

/**
 * Given a list of edges, topologically sorts them while using initial source
 * as the start edge.
 */
function sortEdges(edges: Edge[], initialSource: string): Edge[] {
  const visited = new Set<Edge>();
  const stack: Edge[] = [];
  const edgeMap = groupBy((edge) => edge.source, edges);

  /**
   * Recursive topological sort helper
   */
  const topoSortHelper = (edge: Edge, visited: Set<Edge>, stack: Edge[]) => {
    visited.add(edge);
    stack.push(edge);

    // will be empty if target edge is a terminal edge
    if (edgeMap[edge.target] != null) {
      edgeMap[edge.target].forEach((targetEdge) => {
        if (!visited.has(targetEdge)) {
          topoSortHelper(targetEdge, visited, stack);
        }
      });
    }
  };

  // There may be more than one edge where source is the initial step
  const [initialEdges, restEdges] = partition((edge) => edge.source === initialSource, edges);

  // Place initial edges at start to ensure toposort has right origin
  initialEdges.concat(restEdges).forEach((edge) => {
    if (!visited.has(edge)) {
      topoSortHelper(edge, visited, stack);
    }
  });

  return stack;
}

/**
 * Builds a map of steps with parent and child relations.
 */
const buildGraph = (
  stepsToTraverse: VersionDiffCombinedStep[],
  initialStepName: string,
  options: { showVerifyWithPersona?: boolean },
): Record<string, VersionDiffTreeNode> => {
  const stepMap: Record<string, VersionDiffCombinedStep> = keyBy('token', stepsToTraverse);

  // Generate edges for establishing step hierarch
  const { edges } = traverse(
    stepMap,
    options,
  )({
    visitedMap: mapValues(() => false, stepMap),
    stepsToTraverse,
    edges: [],
  });

  const tokensToNode = stepsToTraverse.reduce<Record<string, VersionDiffTreeNode>>((map, step) => {
    map[step.token] = {
      step,
      parent: undefined,
      retryParents: [],
      children: [],
    };
    return map;
  }, {});

  const sortedEdges = sortEdges(edges, initialStepName);

  // Add parent and child relationships via edges
  sortedEdges.forEach(({ target, source, workflowOrComponentToken }) => {
    if (target === PLACEHOLDER_STEP_NAME) {
      tokensToNode[source].children.push({
        target,
        workflowOrComponentToken,
      });
    }
    // skip if target or source not found in tokensToNode
    if (!tokensToNode[source]) {
      return;
    }
    // for inquiry loop steps, we need to recursively process all targets
    if (!tokensToNode[target]) {
      const processedNodes = new Set<string>();

      const processTargets = (currentTarget: string) => {
        if (processedNodes.has(currentTarget)) {
          return;
        }
        processedNodes.add(currentTarget);

        const targets = edges.filter((edge) => edge.source === currentTarget);
        targets.forEach(({ target: newTarget, source: _ }) => {
          if (tokensToNode[newTarget]) {
            tokensToNode[newTarget].parent = source;
            tokensToNode[source].children.push({ target: newTarget, workflowOrComponentToken });
            processTargets(newTarget);
          }
        });
      };
      processTargets(target);
    } else {
      tokensToNode[target].parent = source;
      tokensToNode[source].children.push({
        target,
        workflowOrComponentToken,
      });
    }
  });

  return tokensToNode;
};

interface Edge {
  source: string;
  target: string;
  // The last workflow action token/component name used to transition to target
  workflowOrComponentToken: string | undefined;
}

interface TraverseArgs {
  visitedMap: Record<string, boolean>;
  stepsToTraverse: VersionDiffCombinedStep[];
  edges: Edge[];
}

/**
 * Does a breadth-first traversal of the graph to build nodes and edges.
 */
const traverse = (
  stepMap: Record<string, VersionDiffCombinedStep>,
  options: { showVerifyWithPersona?: boolean } = {},
) => {
  const traverseRecursive = ({
    visitedMap,
    stepsToTraverse,
    edges,
  }: TraverseArgs): TraverseArgs => {
    // Base case: return from recursion
    if (stepsToTraverse.length === 0) {
      return {
        visitedMap,
        stepsToTraverse,
        edges,
      };
    }

    const [curr, ...restSteps] = stepsToTraverse;

    // This graph may be cyclic. Ignore revisited steps.
    if (visitedMap[curr.token]) {
      return traverseRecursive({
        visitedMap,
        stepsToTraverse: restSteps,
        edges,
      });
    }

    const newEdges: Edge[] = [];
    switch (curr.type) {
      case UiStepType.Complete:
        // complete is a non-UI terminal step that will never transition to another
        break;
      case UiStepType.Ui: {
        // For every component that can trigger a transition, add an edge.
        uiStepAllComponentConfigs(curr)
          .filter(isUiTransitionComponentConfig)
          .filter(
            (component) =>
              component.type !== UiComponentConfigType.ButtonVerifyWithPersona ||
              options?.showVerifyWithPersona,
          )
          .forEach((component) => {
            if (component.config['to-step']) {
              newEdges.push({
                source: curr.token,
                target: component.config['to-step'],
                workflowOrComponentToken: component.name,
              });
              // should always be mutually exclusive
            } else if (component.config['workflow-version-id']) {
              newEdges.push({
                source: curr.token,
                target: component.config['workflow-version-id'],
                workflowOrComponentToken: component.name,
              });
            } else {
              newEdges.push({
                source: curr.token,
                target: PLACEHOLDER_STEP_NAME,
                workflowOrComponentToken: component.name,
              });
            }
          });
        break;
      }
      case UiStepType.Document:
      case UiStepType.Integration:
      case UiStepType.VerifyWithPersona:
      case UiStepType.Selfie:
      case UiStepType.SelfieReflection:
      case UiStepType.GovernmentId: {
        if (curr.config['to-step']) {
          newEdges.push({
            source: curr.token,
            target: curr.config['to-step'],
            workflowOrComponentToken: curr.name,
          });
          // should always be mutually exclusive
        } else if (curr.config['workflow-version-id']) {
          newEdges.push({
            source: curr.token,
            target: curr.config['workflow-version-id'],
            workflowOrComponentToken: curr.name,
          });
        }
        break;
      }
      case 'workflow': {
        newEdges.push(
          ...curr.nextSteps.filter(Boolean).map((nextStep) => ({
            source: curr.token,
            target: nextStep,
            workflowOrComponentToken: curr.updateInquiryNextStepActionStepToken,
          })),
        );
        break;
      }
      case UiStepType.Loop: {
        const loopSteps = mapStepsPreviewAndToken({
          steps: curr.config.steps,
          stepsPreviewMap: {},
          previewPageMap: {},
        });
        newEdges.push(
          ...loopSteps.map((step) => ({
            source: curr.token,
            target: step.token,
            workflowOrComponentToken: curr.name,
          })),
        );
        return traverseRecursive({
          visitedMap: set(curr.token, true, visitedMap),
          stepsToTraverse: restSteps.concat(loopSteps),
          edges: edges.concat(newEdges),
        });
      }
      default:
        assertNever(curr);
    }

    // Check for edges linked to nodes that do not exist
    // This implies that a step was removed without updating it s parent
    const [_orphanEdges, existingNewEdges] = partition(
      (edge) => edge.target !== PLACEHOLDER_STEP_NAME && stepMap[edge.target] == null,
      newEdges,
    );
    // Forbid self cycles
    const [_selfCycleEdges, validNewEdges] = partition(
      (edge) => edge.target === edge.source,
      existingNewEdges,
    );

    const newStepsToTraverse = validNewEdges
      .filter((edge) => edge.target !== PLACEHOLDER_STEP_NAME)
      .map((edge) => stepMap[edge.target]);

    return traverseRecursive({
      visitedMap: set(curr.token, true, visitedMap),
      stepsToTraverse: restSteps.concat(newStepsToTraverse),
      edges: edges.concat(newEdges),
    });
  };

  return traverseRecursive;
};

const buildTree = (step: VersionDiffCombinedStep, graph: Record<string, VersionDiffTreeNode>) =>
  buildTreeRecursive(new Set<string>(), step, graph);

/**
 * Builds tree from ITV/workflow steps which visually represents the Inquiry flow.
 * Notes:
 * 1. uses visited Set which is mutated to ensure visited steps return only themselves
 *    upon subsequent visits rather than duplicating the subtree.
 *    (needed for rendering connectors, which are visual representations of step pointers)
 * 2. omits update_next_step_actions as these are now implicitly represented by branching node connectors
 * 3. adds support for branches originating from non-workflow steps
 *    (screens with multiple buttons or workflow_on_error_step_name)
 *
 * @returns combined screen + workflow steps used to render ScreenFlow in ITE flow editor
 */
const buildTreeRecursive = (
  visited: Set<string>,
  step: VersionDiffCombinedStep,
  graph: Record<string, VersionDiffTreeNode>,
  parentStepName?: string,
  workflowOrComponentToken?: string,
): CombinedVersionDiffStepForTree[] => {
  // Can be null if attempting to recurse the empty branch placeholder, as there
  // is no step for this empty branch
  if (step == null) return [];

  /**
   * return just the current step if step has already been visited.
   * avoids duplicate subtrees, instead renders a reference to an existing screen (connector)
   */
  if (visited.has(step.token)) {
    return [
      {
        ...step,
        isConnector: true,
        parentStepName,
        workflowOrComponentToken,
      },
    ];
  }

  visited.add(step.token);
  const hasBranches = graph[step.token].children.length > 1;

  /**
   * UiStepType.Complete is a non-UI terminal step. For all other step types, return:
   * - step + recursive remainder of branch OR
   * - step with branches (+recursive branch paths)
   */
  switch (step.type) {
    case UiStepType.Complete:
      return [step];
    case UiStepType.Document:
    case UiStepType.Integration:
    case UiStepType.VerifyWithPersona:
    case UiStepType.GovernmentId:
    case UiStepType.Selfie:
    case UiStepType.SelfieReflection:
    case UiStepType.Ui: {
      /**
       * branches for UI steps occur when:
       * - the step has multiple buttons OR
       * - a button on the step transitions to a workflow, as the
       *   workflow_on_error_step_name attribute is then required
       */

      return hasBranches
        ? [
            {
              ...step,
              branches: graph[step.token].children.map((child) => {
                return buildTreeRecursive(
                  visited,
                  graph[child.target]?.step,
                  graph,
                  step.name,
                  child.workflowOrComponentToken,
                );
              }),
              parentStepName,
              workflowOrComponentToken,
            },
          ]
        : [
            { ...step, parentStepName, workflowOrComponentToken },
            ...graph[step.token].children.flatMap((child) =>
              buildTreeRecursive(
                visited,
                graph[child.target]?.step,
                graph,
                step.name,
                child.workflowOrComponentToken,
              ),
            ),
          ];
    }
    case 'workflow': {
      /**
       * workflowStepType is added to the node for stronger typing in ScreenFlow
       * update_next_step actions are omitted, as these aren't rendered in ScreenFlow.
       */
      const currentStep = {
        ...step,
        workflowStepType: step.workflowStep?.type,
      };
      const skipCurrentStep = step.workflowStep.isUpdateNextStepAction;
      return step.workflowStep?.type === WorkflowStepType.Conditional
        ? [
            {
              ...currentStep,
              // map with step.nextSteps over graph[step.token].children to support empty branches
              branches: step.nextSteps.map(
                (nextStepToken) =>
                  nextStepToken != null
                    ? buildTreeRecursive(
                        visited,
                        graph[nextStepToken].step,
                        graph,
                        parentStepName,
                        workflowOrComponentToken,
                      )
                    : [], // still want to display a branch even if its empty
              ),
              parentStepName,
              workflowOrComponentToken,
            },
          ]
        : [
            ...(skipCurrentStep
              ? []
              : [{ ...currentStep, parentStepName, workflowOrComponentToken }]),
            ...graph[step.token].children.flatMap((child) =>
              buildTreeRecursive(
                visited,
                graph[child.target].step,
                graph,
                parentStepName,
                child.workflowOrComponentToken,
              ),
            ),
          ];
    }
    case UiStepType.Loop:
      return [
        step,
        ...graph[step.token].children.flatMap((node) =>
          buildTreeRecursive(visited, graph[node.target]?.step, graph),
        ),
      ];
    default:
      assertNever(step);
  }
};

/**
 * Extracts two variants of visual logic representation in ScreenFlow from workflow versions in template.
 * 1. Actions node: linear sequence of (typically action) workflow steps
 * 2. Conditional node: conditional step + branches
 *
 * @returns list of linear action steps or conditional branching used to build template graph
 */
const extractNextStepsAndConditionals = (
  steps: WorkflowStep[],
  workflowVersionId: string,
): VersionDiffLinkedWorkflowStep[] => {
  // Workflow can have empty steps if last node was deleted from a wfv
  if (!steps.length) {
    return [];
  }

  // Reorder steps for the base case so update inquiry next step action is last.
  // This prevents skipCurrentStep from being true and omitting the rest of the actions inside of buildTree
  steps = getReorderedSteps(steps);

  const currentNode: VersionDiffLinkedWorkflowStep = {
    type: 'workflow',
    // append wfv prefix in front of step token to disambiguate steps with the same token but from different workflow versions
    // this can arise when wfvs are merged due to deletion of a screen step that was in the middle
    token: `${workflowVersionId}.${steps[0].token}`,
    workflowVersionId,
    workflowStep: {
      token: steps[0].token,
      type: steps[0].type,
      name: steps[0].name,
      isUpdateNextStepAction: isUpdateInquiryNextStepActionStep(steps[0]),
    },
    nextSteps: [],
  };
  const branchStep = steps.find(
    (step): step is WorkflowStepConditional => step.type === WorkflowStepType.Conditional,
  );

  if (branchStep == null) {
    const updateInquiryNextStep = steps.find(isUpdateInquiryNextStepActionStep);
    currentNode.nextSteps =
      updateInquiryNextStep?.action?.params['next-step-name'] != null
        ? [updateInquiryNextStep?.action?.params['next-step-name']]
        : [];
    currentNode.updateInquiryNextStepActionStepToken = updateInquiryNextStep?.token;
    return [currentNode];
  } else {
    const branchStepNode: VersionDiffLinkedWorkflowStep = {
      type: 'workflow',
      token: `${workflowVersionId}.${branchStep.token}`,
      workflowVersionId,
      workflowStep: {
        token: branchStep.token,
        type: WorkflowStepType.Conditional,
        name: branchStep.name,
      },
      // purposefully leave null token values for empty branches, as they're used in buildTreeRecursive to render empty branches
      nextSteps: branchStep.branches
        .map((branch) => getBranchToken(branch, workflowVersionId))
        .filter((s): s is string => s != null),
    };
    // no longer need to reorder branch steps here because we do it at beginning of extractNextStepsAndConditionals
    const branchNodes: VersionDiffLinkedWorkflowStep[] = branchStep.branches
      .filter((branch) => branch.length)
      .flatMap((branch) => extractNextStepsAndConditionals(branch, workflowVersionId));
    // omit duplicate initial node when initial workflow step is conditional
    if (branchStep.token === steps[0].token) {
      return [branchStepNode, ...branchNodes];
    } else {
      return [
        {
          ...currentNode,
          nextSteps: [`${workflowVersionId}.${branchStep.token}`],
        },
        branchStepNode,
        ...branchNodes,
      ];
    }
  }
};

export const extractActionsAndConditionalNodes = (
  workflowVersionMap: WorkflowVersionMap,
): VersionDiffLinkedWorkflowStep[] => {
  return Object.values(workflowVersionMap)
    .flatMap((workflowVersion) => {
      if (workflowVersion.attributes['steps-config'] == null) {
        return [];
      }

      const allWorkflowSteps: VersionDiffLinkedWorkflowStep[] = extractNextStepsAndConditionals(
        workflowVersion.attributes['steps-config'],
        workflowVersion.id,
      );

      // Workflow can have empty steps if last node was deleted from a wfv
      if (allWorkflowSteps.length === 0) {
        return [];
      }

      // update first step token to workflowVersion token before returning
      return [
        {
          ...allWorkflowSteps[0],
          token: workflowVersion.id,
        },
      ].concat(allWorkflowSteps.slice(1));
    })
    .filter(Boolean);
};

/**
 * @param steps server representation of ITV steps data
 * @param stepsPreview client data representation of ITV steps
 * @param pages preview component to render for each step in tree, props populated via stepsPreview
 * @param extractedWorkflowSteps action seqeuence + branch node workflow steps
 */
type BuildTreeAndGraphParams = {
  steps: InquiryTemplateVersionStep[];
  stepsPreviewMap: StepPreviewMap;
  previewPageMap: PreviewPageMap;
  extractedWorkflowSteps: VersionDiffLinkedWorkflowStep[];
  isITEInternal: boolean;
};

export const buildDiffTreeAndGraph = ({
  steps,
  stepsPreviewMap,
  previewPageMap,
  extractedWorkflowSteps,
  isITEInternal,
}: BuildTreeAndGraphParams): [
  CombinedVersionDiffStepForTree[],
  Record<string, VersionDiffTreeNode>,
] => {
  const initialStep = firstInitialStep(steps);
  if (initialStep == null) {
    return [[], {}];
  }
  const VersionDiffCombinedSteps = [
    ...mapStepsPreviewAndToken({ steps, stepsPreviewMap, previewPageMap }),
    ...extractedWorkflowSteps,
  ];

  const treeRootStep = VersionDiffCombinedSteps.find((step) => step.token === initialStep.name)!;

  const graph = buildGraph(VersionDiffCombinedSteps, initialStep.name, {
    showVerifyWithPersona: isITEInternal,
  });
  const tree = buildTree(treeRootStep, graph);
  return [tree, graph];
};

export const changeTypeForColumn = (
  changeColumn: Column,
  changeType: VersionDiffChangeType | undefined,
) => {
  switch (changeType) {
    case VersionDiffChangeType.ADDED:
      return changeColumn === Column.TARGET ? VersionDiffChangeType.ADDED : null;
    case VersionDiffChangeType.REMOVED:
      return changeColumn === Column.BASE ? VersionDiffChangeType.REMOVED : null;
    case VersionDiffChangeType.MODIFIED:
      return VersionDiffChangeType.MODIFIED;
    default:
      return null;
  }
};

export const valueDiffIsNotEmpty = <T>(
  valueDiff: ValueDiff<T> | null,
): valueDiff is ValueDiff<T> & { added: T; removed: T } => {
  return !(valueDiff == null) && valueDiff.added != null && valueDiff.removed != null;
};

export const setDiffIsNotEmpty = <T>(
  setDiff: SetDiff<T> | null,
): setDiff is SetDiff<T> & { added: T[]; removed: T[] } => {
  return !(setDiff == null) && Array.isArray(setDiff.added) && Array.isArray(setDiff.removed);
};

interface VersionDiffNavigationConextType {
  stepsViewRef: RefObject<HTMLDivElement> | null;
  verificationTemplateVersionIdToRefMap: MutableRefObject<
    Record<string, RefObject<HTMLDivElement>>
  > | null;
  actionSequenceTokenToRefMap: MutableRefObject<Record<string, RefObject<HTMLDivElement>>> | null;
  selectedStepDifferStep: SelectedStep | null;
  setSelectedStepDifferStep: Dispatch<SetStateAction<SelectedStep | null>>;
}

export const VersionDiffNavigationContext = createContext<VersionDiffNavigationConextType>({
  stepsViewRef: null,
  verificationTemplateVersionIdToRefMap: null,
  actionSequenceTokenToRefMap: null,
  selectedStepDifferStep: null,
  setSelectedStepDifferStep: noop,
});
